import type { CSSProperties } from "react";
import { useLocale } from "@genius-tracker/i18n/react";

/**
 * GT-CUSTOMER-NOTIFICATIONS-WEB-INAPP-04 (Wave D) — unread-count
 * badge for the top-bar bell.
 *
 * Contract (Phase 0 §8.2):
 *   - hidden at 0,
 *   - numeric 1..99,
 *   - "99+" (localized overflow glyph) above 99,
 *   - `aria-hidden` — the accessible count lives in the bell link's
 *     `aria-label` (`notifications.center.bell.aria`) so screen
 *     readers never announce the number twice.
 *
 * No badge primitive exists in the design system yet (verified in
 * Phase 0 §3 row 5); this is deliberately a tiny local component.
 * If a second consumer appears, extraction to
 * `packages/design-system` is the named follow-up (AGENTS.md §10).
 */
export function NotificationBadge({ count }: { count: number }) {
  const { t } = useLocale();
  if (count <= 0) return null;
  const display = count > 99 ? t("notifications.center.badge.overflow") : String(count);
  return (
    <span aria-hidden data-testid="notification-badge" style={badgeStyle}>
      {display}
    </span>
  );
}

const badgeStyle: CSSProperties = {
  position: "absolute",
  insetBlockStart: -5,
  insetInlineEnd: -5,
  minInlineSize: 16,
  blockSize: 16,
  paddingInline: 4,
  borderRadius: 999,
  background: "var(--gt-sys-danger)",
  // `accent-contrast` is the design system's "text on a strong
  // colored surface" token (white in light, near-black in dark,
  // `Canvas` in forced-colors) — no raw hex.
  color: "var(--gt-sys-accent-contrast)",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
};
